import React, { useCallback, useEffect, useState } from 'react'; // eslint-disable-line
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useMentorSession } from '../context/TrackerContext';
import { getMyTasks, updateTaskStatus, getPendingClassLogs, getMentorNotices, markNoticeRead } from '../lib/api';
import type { Task, Notice } from '../lib/api';

const ANIM_CSS = `
@keyframes qaFadeUp    { from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);} }
@keyframes qaPop       { 0%{transform:scale(.86);opacity:0;}60%{transform:scale(1.04);}100%{transform:scale(1);opacity:1;} }
@keyframes qaShake     { 0%,100%{transform:translateX(0);}20%,60%{transform:translateX(-5px);}40%,80%{transform:translateX(5px);} }
@keyframes qaPulse     { 0%,100%{opacity:1;}50%{opacity:.4;} }
@keyframes qaSlide     { from{opacity:0;transform:translateX(-8px);}to{opacity:1;transform:translateX(0);} }
@keyframes qaGlow      { 0%,100%{box-shadow:0 0 0 0 rgba(124,58,237,.25);}50%{box-shadow:0 0 0 8px rgba(124,58,237,0);} }
@keyframes qaSlideUp   { from{opacity:0;transform:translateY(60px) scale(.96);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes qaFadeIn    { from{opacity:0;}to{opacity:1;} }
@keyframes qaConfetti  { 0%{transform:translateY(0) rotate(0);opacity:1;}100%{transform:translateY(100px) rotate(720deg);opacity:0;} }
@keyframes qaRocket    { 0%{transform:translateY(0) scale(1);}50%{transform:translateY(-18px) scale(1.15);}100%{transform:translateY(0) scale(1);} }
@keyframes qaStarburst { 0%{transform:scale(0) rotate(-30deg);opacity:0;}60%{transform:scale(1.18) rotate(8deg);opacity:1;}100%{transform:scale(1) rotate(0);opacity:1;} }
@keyframes qaUrgentRing{ 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.5);}70%{box-shadow:0 0 0 10px rgba(220,38,38,0);} }
@keyframes qaGradShift { 0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;} }
@keyframes qaFloat     { 0%,100%{transform:translateY(0);}50%{transform:translateY(-12px);} }
.qa-card    { animation: qaFadeUp .35s ease both; }
.qa-pop     { animation: qaPop .28s cubic-bezier(.34,1.56,.64,1) both; }
.qa-shake   { animation: qaShake 1.4s ease-in-out infinite; }
.qa-pulse   { animation: qaPulse 1.8s ease infinite; }
.qa-slide   { animation: qaSlide .2s ease both; }
.qa-glow    { animation: qaGlow 2s ease-in-out infinite; }
.qa-slideup { animation: qaSlideUp .42s cubic-bezier(.34,1.28,.64,1) both; }
.qa-fadein  { animation: qaFadeIn .25s ease both; }
.qa-urgent  { animation: qaUrgentRing 1.6s ease-in-out infinite; }
`;

const PRIORITY_CONFIG: Record<string, { color: string; bg: string; border: string }> = {
  Low:    { color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
  Medium: { color: '#b45309', bg: '#fefce8', border: '#fde68a' },
  High:   { color: '#c2410c', bg: '#fff7ed', border: '#fdba74' },
  Urgent: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
};

const NOTICE_PRIORITY: Record<string, { color: string; bg: string; ring: string; icon: string }> = {
  Info:      { color: '#2563eb', bg: '#eff6ff', ring: '#93c5fd', icon: 'ℹ' },
  Important: { color: '#b45309', bg: '#fffbeb', ring: '#fde68a', icon: '⚠' },
  Urgent:    { color: '#dc2626', bg: '#fef2f2', ring: '#fca5a5', icon: '🚨' },
};

function fmtDate(d: string) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
function daysUntil(d: string): number {
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
}
function fmtAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins/60)}h ago`;
  return `${Math.round(mins/1440)}d ago`;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg: Record<string, { bg: string; color: string }> = {
    Pending:       { bg: '#f5f3ff', color: '#7c3aed' },
    'In Progress': { bg: '#fffbeb', color: '#d97706' },
    Done:          { bg: '#f0fdf4', color: '#16a34a' },
    Cancelled:     { bg: '#f9fafb', color: '#6b7280' },
  };
  const s = cfg[status] || cfg.Pending;
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>
      {status}
    </span>
  );
};

interface DoneModalState { taskId: string; title: string }

const MentorQuickActions: React.FC = () => {
  const { mentorId, mentorName } = useMentorSession();

  const [tab, setTab] = useState<'tasks' | 'logs' | 'notices'>('tasks');

  /* Tasks */
  const [tasks, setTasks]           = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [doneModal, setDoneModal]   = useState<DoneModalState | null>(null);
  const [doneNotes, setDoneNotes]   = useState('');
  const [doneStatus, setDoneStatus] = useState<'Done' | 'In Progress'>('Done');
  const [savingDone, setSavingDone] = useState(false);

  /* Logs */
  const [pendingLogs, setPendingLogs] = useState<{ studentId: string; studentName: string; date: string; dayName: string; slotStart: string }[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  /* Notices */
  const [notices, setNotices]         = useState<(Notice & { isRead: boolean })[]>([]);
  const [loadingNotices, setLoadingNotices] = useState(false);
  const [noticesLoaded, setNoticesLoaded]   = useState(false);

  /* Celebration overlay */
  const [celebType,    setCelebType]    = useState<null | 'progress' | 'done'>(null);
  const [celebTask,    setCelebTask]    = useState('');

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    try { const res = await getMyTasks(mentorId); setTasks(res.tasks); }
    catch (err: any) { toast.error(err.message); }
    finally { setLoadingTasks(false); }
  }, [mentorId]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try { const res = await getPendingClassLogs(mentorId); setPendingLogs(res.pending); }
    catch (err: any) { toast.error(err.message); }
    finally { setLoadingLogs(false); }
  }, [mentorId]);

  const loadNotices = useCallback(async () => {
    setLoadingNotices(true);
    try { const res = await getMentorNotices(mentorId); setNotices(res.notices); setNoticesLoaded(true); }
    catch (err: any) { toast.error(err.message); }
    finally { setLoadingNotices(false); }
  }, [mentorId]);

  /* Load tasks + notices on mount */
  useEffect(() => {
    const run = async () => {
      setLoadingTasks(true);
      setLoadingNotices(true);
      try {
        const [tRes, nRes] = await Promise.allSettled([
          getMyTasks(mentorId),
          getMentorNotices(mentorId),
        ]);
        if (tRes.status === 'fulfilled') setTasks(tRes.value.tasks);
        if (nRes.status === 'fulfilled') { setNotices(nRes.value.notices); setNoticesLoaded(true); }
      } finally {
        setLoadingTasks(false);
        setLoadingNotices(false);
      }
    };
    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorId]);

  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);
  useEffect(() => { if (tab === 'notices' && !noticesLoaded) loadNotices(); }, [tab, noticesLoaded, loadNotices]);

  const handleMarkRead = async (n: Notice & { isRead: boolean }) => {
    if (n.isRead) return;
    try {
      await markNoticeRead(mentorId, n.noticeId);
      setNotices(prev => prev.map(x => x.noticeId === n.noticeId ? { ...x, isRead: true } : x));
    } catch {}
  };

  const handleMarkDone = async () => {
    if (!doneModal) return;
    setSavingDone(true);
    try {
      await updateTaskStatus(mentorId, doneModal.taskId, doneStatus, doneNotes);
      const title = doneModal.title;
      setDoneModal(null); setDoneNotes('');
      loadTasks();
      // Trigger celebration animation
      setCelebTask(title);
      setCelebType(doneStatus === 'Done' ? 'done' : 'progress');
      setTimeout(() => setCelebType(null), doneStatus === 'Done' ? 3500 : 2500);
    } catch (err: any) { toast.error(err.message); }
    finally { setSavingDone(false); }
  };

  const activeTasks   = tasks.filter(t => t.status !== 'Done' && t.status !== 'Cancelled');
  const doneTasks     = tasks.filter(t => t.status === 'Done');
  const urgentCount   = activeTasks.filter(t => t.priority === 'Urgent' || t.priority === 'High').length;
  const unreadNotices = notices.filter(n => !n.isRead).length;


  const TABS = [
    { key: 'tasks' as const,   label: `My Tasks`,          badge: activeTasks.length || undefined },
    { key: 'logs' as const,    label: `Unlogged Classes`,   badge: pendingLogs.length || undefined },
    { key: 'notices' as const, label: `Notices`,            badge: unreadNotices || undefined, badgeColor: '#2563eb' },
  ];

  return (
    <TrackerLayout>
      <style>{ANIM_CSS}</style>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#312741' }}>Quick Actions</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Hi {mentorName}! You have <span className="font-semibold text-purple-700">{activeTasks.length} active tasks</span>
              {urgentCount > 0 && <span className="font-semibold text-red-600 ml-1">({urgentCount} urgent)</span>}
              {unreadNotices > 0 && <span className="font-semibold text-blue-600 ml-1"> · {unreadNotices} new notice{unreadNotices !== 1 ? 's' : ''}</span>}
            </p>
          </div>
          <button
            onClick={() => {
              if (tab === 'tasks') loadTasks();
              else if (tab === 'logs') loadLogs();
              else loadNotices();
            }}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-gray-200 text-xs font-bold text-gray-500 hover:bg-gray-50 transition-all">
            ↻ Refresh
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Active Tasks',     val: activeTasks.length,   color: '#7c3aed', bg: '#f5f3ff' },
            { label: 'Urgent / High',    val: urgentCount,          color: urgentCount > 0 ? '#dc2626' : '#6b7280', bg: urgentCount > 0 ? '#fef2f2' : '#f9fafb' },
            { label: 'Completed',        val: doneTasks.length,     color: '#16a34a', bg: '#f0fdf4' },
            { label: 'New Notices',      val: unreadNotices,        color: unreadNotices > 0 ? '#2563eb' : '#6b7280', bg: unreadNotices > 0 ? '#eff6ff' : '#f9fafb' },
          ].map((s, i) => (
            <div key={s.label} className="qa-card rounded-xl border p-4 text-center"
              style={{ animationDelay:`${i*60}ms`, background:s.bg, borderColor:s.bg }}>
              <p className="text-2xl font-black" style={{ color:s.color }}>{s.val}</p>
              <p className="text-[11px] text-gray-500 mt-0.5 font-medium">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className="relative px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={tab === t.key
                ? { background:'#312741', color:'#ffe500', boxShadow:'0 2px 8px rgba(49,39,65,.3)' }
                : { color:'#6b7280' }}>
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span className="ml-1.5 text-[10px] font-black px-1.5 py-0.5 rounded-full"
                  style={{ background: t.badgeColor || '#d97706', color:'#fff' }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── TASKS TAB ── */}
        {tab === 'tasks' && (
          <div className="space-y-3">
            {loadingTasks ? (
              <div className="text-center py-12 text-gray-400">Loading tasks…</div>
            ) : activeTasks.length === 0 && doneTasks.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">🎉</p>
                <p className="text-gray-500 font-medium">No tasks assigned yet</p>
                <p className="text-gray-400 text-sm">Your admin or coordinator will assign work here</p>
              </div>
            ) : (
              <>
                {activeTasks.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Active Tasks</p>
                    <div className="grid gap-3">
                      {activeTasks.map((task, idx) => {
                        const p = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Medium;
                        const overdue = task.dueDate && daysUntil(task.dueDate) < 0 && task.status !== 'Done';
                        const dueDays = task.dueDate ? daysUntil(task.dueDate) : null;
                        return (
                          <div key={task.taskId} className={`qa-card bg-white rounded-xl border-2 p-4 ${task.priority === 'Urgent' ? 'qa-glow' : ''}`}
                            style={{ animationDelay:`${idx*70}ms`, borderColor: overdue ? '#fca5a5' : p.border }}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${task.priority === 'Urgent' ? 'qa-shake' : ''}`}
                                  style={{ background:p.bg, color:p.color, border:`1px solid ${p.border}` }}>
                                  {task.priority === 'Urgent' ? '🚨 ' : ''}{task.priority}
                                </span>
                                <StatusBadge status={task.status} />
                                {overdue && (
                                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full qa-pulse" style={{ background:'#fef2f2', color:'#dc2626' }}>
                                    Overdue!
                                  </span>
                                )}
                              </div>
                              <button onClick={() => { setDoneModal({ taskId:task.taskId, title:task.title }); setDoneStatus('Done'); setDoneNotes(''); }}
                                className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-bold transition-all hover:scale-105 active:scale-95"
                                style={{ background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff' }}>
                                Mark Update
                              </button>
                            </div>
                            <h3 className="font-bold text-gray-800 text-sm mb-1">{task.title}</h3>
                            {task.description && <p className="text-xs text-gray-500 mb-2 leading-relaxed">{task.description}</p>}
                            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                              <span>By: <span className="font-semibold text-gray-700">{task.assignedByName}</span></span>
                              {task.dueDate && (
                                <span style={{ color: overdue ? '#dc2626' : dueDays !== null && dueDays <= 2 ? '#d97706' : '#6b7280' }}>
                                  Due: <span className="font-semibold">{fmtDate(task.dueDate)}</span>
                                  {dueDays !== null && !overdue && dueDays <= 3 && ` (${dueDays}d left)`}
                                  {overdue && ` (overdue)`}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {doneTasks.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-bold text-gray-400 uppercase tracking-widest py-2 hover:text-gray-600">
                      Completed Tasks ({doneTasks.length})
                    </summary>
                    <div className="grid gap-2 mt-2">
                      {doneTasks.map((task, idx) => (
                        <div key={task.taskId} className="qa-card bg-gray-50 rounded-xl border border-gray-200 p-3" style={{ animationDelay:`${idx*50}ms` }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[11px] font-bold text-green-700">✓ Done</span>
                            <span className="text-[11px] text-gray-400">{task.title}</span>
                          </div>
                          {task.completionNotes && (
                            <p className="text-xs text-gray-500 bg-white border border-gray-100 rounded-lg px-3 py-1.5">{task.completionNotes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
        )}

        {/* ── UNLOGGED CLASSES TAB ── */}
        {tab === 'logs' && (
          <div className="space-y-3">
            {loadingLogs ? (
              <div className="text-center py-12 text-gray-400">Checking class logs…</div>
            ) : pendingLogs.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">✅</p>
                <p className="text-gray-500 font-medium">All classes logged!</p>
                <p className="text-gray-400 text-sm">No missing class logs today</p>
              </div>
            ) : (
              (() => {
                const grouped: Record<string, typeof pendingLogs> = {};
                for (const p of pendingLogs) {
                  if (!grouped[p.date]) grouped[p.date] = [];
                  grouped[p.date].push(p);
                }
                const sortedDates = Object.keys(grouped).sort((a,b) => b.localeCompare(a));
                let cardIdx = 0;
                return (
                  <>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800">
                      <strong>⚠ {pendingLogs.length} unlogged class session{pendingLogs.length !== 1 ? 's' : ''}</strong> found today.
                      Please go to <strong>Log a Class</strong> to submit the missing logs.
                    </div>
                    {sortedDates.map(date => {
                      const items = grouped[date];
                      const { dayName } = items[0];
                      const isToday = date === new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
                      return (
                        <div key={date} className="space-y-2">
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-xs font-black uppercase tracking-widest text-orange-600">{dayName} — {date}</span>
                            {isToday && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background:'#fef3c7', color:'#b45309' }}>Today</span>}
                            <span className="text-[10px] text-gray-400 font-semibold">{items.length} session{items.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="grid gap-2 pl-2 border-l-2 border-orange-200">
                            {items.map(p => {
                              const delay = cardIdx++ * 50;
                              const parts = (p.slotStart || '').split(':').map(Number);
                              const hh = parts[0] || 0; const mm = parts[1] || 0;
                              const timeLabel = p.slotStart ? `${hh%12||12}:${String(mm).padStart(2,'0')} ${hh>=12?'PM':'AM'}` : '—';
                              return (
                                <div key={`${p.studentId}-${p.date}-${p.slotStart}`}
                                  className="qa-card bg-white rounded-xl border border-orange-200 p-3 flex items-center justify-between"
                                  style={{ animationDelay:`${delay}ms` }}>
                                  <div>
                                    <p className="font-semibold text-sm text-gray-800">{p.studentName}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Slot: <span className="font-semibold text-gray-600">{timeLabel}</span></p>
                                  </div>
                                  <span className="text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0" style={{ background:'#fff7ed', color:'#c2410c' }}>Not Logged</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-gray-400 text-center">Tip: Use <strong>Log a Class</strong> from the sidebar to quickly log these sessions.</p>
                  </>
                );
              })()
            )}
          </div>
        )}

        {/* ── NOTICES TAB ── */}
        {tab === 'notices' && (
          <div className="space-y-3">
            {loadingNotices ? (
              <div className="text-center py-12 text-gray-400">Loading notices…</div>
            ) : notices.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">📢</p>
                <p className="text-gray-500 font-medium">No notices yet</p>
                <p className="text-gray-400 text-sm">Admin announcements will appear here</p>
              </div>
            ) : (
              <>
                {unreadNotices > 0 && (
                  <p className="text-xs font-bold text-blue-600 uppercase tracking-widest">{unreadNotices} unread</p>
                )}
                {notices.map((n, i) => {
                  const cfg = NOTICE_PRIORITY[n.priority] || NOTICE_PRIORITY.Info;
                  return (
                    <div key={n.noticeId}
                      className={`qa-card rounded-xl border-l-4 p-4 cursor-pointer transition-all hover:shadow-md ${!n.isRead ? 'shadow-sm' : 'opacity-75'}`}
                      style={{
                        animationDelay:`${i*50}ms`,
                        borderLeftColor: cfg.color,
                        borderTopColor: n.isRead ? '#e5e7eb' : cfg.ring,
                        borderRightColor: n.isRead ? '#e5e7eb' : cfg.ring,
                        borderBottomColor: n.isRead ? '#e5e7eb' : cfg.ring,
                        borderWidth: 1,
                        background: n.isRead ? '#fafafa' : cfg.bg,
                      }}
                      onClick={() => handleMarkRead(n)}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {n.pinned && <span className="text-sm">📌</span>}
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: cfg.bg, color: cfg.color, border:`1px solid ${cfg.ring}` }}>
                            {cfg.icon} {n.priority}
                          </span>
                          {!n.isRead && (
                            <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                              style={{ background:'#2563eb', color:'#fff' }}>NEW</span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 flex-shrink-0">{fmtAgo(n.createdAt)}</p>
                      </div>
                      <p className="font-bold text-gray-800 text-sm leading-snug">{n.title}</p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{n.content}</p>
                      <p className="text-[10px] text-gray-400 mt-2">from {n.createdByName}</p>
                      {!n.isRead && (
                        <p className="text-[10px] text-blue-500 font-semibold mt-1">Tap to mark as read</p>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* ══ CELEBRATION OVERLAY ══ */}
      {celebType && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center pointer-events-none">
          {/* Confetti for "Done" */}
          {celebType === 'done' && (
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              {Array.from({ length: 22 }).map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${5 + (i * 17) % 92}%`,
                  top: `-${10 + (i * 7) % 30}%`,
                  width: 8 + (i % 4) * 3,
                  height: 8 + (i % 4) * 3,
                  borderRadius: i % 3 === 0 ? '50%' : 3,
                  background: ['#fbbf24','#f87171','#a78bfa','#34d399','#60a5fa','#fb923c','#f472b6'][i % 7],
                  animation: `qaConfetti ${1.4 + (i * 0.12) % 1.2}s ${(i * 0.07)}s ease-in forwards`,
                }} />
              ))}
            </div>
          )}

          {/* Central card */}
          <div className="qa-starburst" style={{
            background: celebType === 'done'
              ? 'linear-gradient(135deg,#065f46,#16a34a,#4ade80)'
              : 'linear-gradient(135deg,#92400e,#d97706,#fbbf24)',
            borderRadius: 28, padding: '32px 28px',
            textAlign: 'center', maxWidth: 300, width: '85%',
            boxShadow: celebType === 'done'
              ? '0 24px 64px rgba(22,163,74,.6)'
              : '0 24px 64px rgba(217,119,6,.6)',
            animation: 'qaStarburst .5s cubic-bezier(.34,1.56,.64,1) both',
          }}>
            <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 12,
              animation: 'qaFloat 2s ease-in-out infinite' }}>
              {celebType === 'done' ? '🎉' : '🚀'}
            </div>
            <p style={{ fontSize: 26, fontWeight: 900, color: '#fff',
              letterSpacing: '-.02em', lineHeight: 1.1, marginBottom: 8,
              textShadow: '0 2px 12px rgba(0,0,0,.3)' }}>
              {celebType === 'done' ? 'Task CRUSHED! 💪' : "You're ON IT! 🔥"}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,.82)',
              fontWeight: 600, lineHeight: 1.5 }}>
              {celebType === 'done'
                ? `"${celebTask.length > 40 ? celebTask.slice(0,40)+'…' : celebTask}"`
                : 'Keep pushing — you got this!'}
            </p>
            {celebType === 'done' && (
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,.65)',
                marginTop: 10, fontWeight: 700 }}>
                Great work, {mentorName.split(' ')[0]}! ✨
              </p>
            )}
          </div>
        </div>
      )}

      {/* Mark Done / Update Modal */}
      {doneModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDoneModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm z-10 qa-pop overflow-hidden">
            <div className="px-6 py-4" style={{ background:'linear-gradient(135deg,#312741,#5a4a6f)' }}>
              <p className="text-white font-bold text-base">Update Task</p>
              <p className="text-white/60 text-xs mt-0.5 truncate">{doneModal.title}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Status</label>
                <div className="flex gap-2">
                  {(['In Progress','Done'] as const).map(s => (
                    <button key={s} type="button" onClick={() => setDoneStatus(s)}
                      className="flex-1 py-2 rounded-lg text-sm font-bold border-2 transition-all"
                      style={doneStatus === s
                        ? { background: s==='Done'?'#16a34a':'#d97706', color:'#fff', borderColor: s==='Done'?'#16a34a':'#d97706' }
                        : { background:'#fff', color:'#6b7280', borderColor:'#e5e7eb' }}>
                      {s === 'Done' ? '✓ Mark Done' : '⚡ In Progress'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Notes {doneStatus === 'Done' ? '(optional)' : ''}
                </label>
                <textarea value={doneNotes} onChange={e => setDoneNotes(e.target.value)}
                  placeholder={doneStatus === 'Done' ? 'What did you complete? Any observations?' : 'What progress did you make?'}
                  rows={3}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm outline-none focus:border-purple-400 resize-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setDoneModal(null)}
                  className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleMarkDone} disabled={savingDone}
                  className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60 transition-all hover:scale-105"
                  style={{ background: doneStatus==='Done' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#d97706,#b45309)' }}>
                  {savingDone ? 'Saving…' : doneStatus==='Done' ? 'Submit Done' : 'Save Progress'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default MentorQuickActions;
